import React, { Fragment, useContext, useEffect, useState, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import screensize from './hooks/resize';
import CloseIcon from "@mui/icons-material/Close";

import AuthContext from "./context/AuthContext";
import { getCompanyId, getCompanyLogo, getUserRole } from './helpers/tokenService';

import AuthGuard from "./components/AuthGuard";
import PublicRoute from "./components/PublicRoute";
import Layout from "./layout/Layout";
import Loader from "./components/common/Loader";
import PWAPrompts from "./components/common/PWAPrompts";

// Lazy-loaded screen components
const Login = lazy(() => import("./Screens/login"));
const AdminDashboard = lazy(() => import("./Screens/Admin_dashboard"));
const EmpDashboard = lazy(() => import("./Screens/Emp_dashboard"));
const OnBoard = lazy(() => import("./Screens/on-board"));
const OnBoardAll = lazy(() => import("./Screens/onboard-all"));
const CreateLetter = lazy(() => import("./Screens/create-letter"));
const DocumentEntry = lazy(() => import("./Screens/document-entry"));
const AssetsDetails = lazy(() => import("./Screens/assets-details"));
const AssetsAssignment = lazy(() => import("./Screens/assets-assignment"));
const Employees = lazy(() => import("./Screens/employees"));
const EmployeeMaster = lazy(() => import("./Screens/employee-master"));
const EmployeeSalaryMaster = lazy(() => import("./Screens/employee-salary-master"));
const EmployeeDetails = lazy(() => import("./Screens/employee-details"));
const EmployeeCTC = lazy(() => import("./Screens/employee-ctc"));
const EmployeeCategory = lazy(() => import("./Screens/employee-category"));
const EmployeeCalendar = lazy(() => import("./Screens/employee-calendar"));
const EmployeeAll = lazy(() => import("./Screens/employee-all"));
const Attendence = lazy(() => import("./Screens/attendence"));
const AttendanceRegularization = lazy(() => import("./Screens/attendence-regularization"));
const SubmitAttendence = lazy(() => import("./Screens/a-submit-attenddence"));
const InOutActivities = lazy(() => import("./Screens/a-in-out-activities"));
const DailyAttendence = lazy(() => import("./Screens/attendence-detail"));
const MonthlyAttendence = lazy(() => import("./Screens/a-monthly-attendence"));
const ManageShift = lazy(() => import("./Screens/a-manage-shift"));
const AllAttendence = lazy(() => import("./Screens/all-attendence"));
const ViewUploadedAttendance = lazy(() => import("./Screens/view-uploaded-attendance"));

const Leave = lazy(() => import("./Screens/leave"));
const ApplyLeave = lazy(() => import("./Screens/l-apply-leave"));
const LeaveBalance = lazy(() => import("./Screens/leave-balance"));
const AllLeaves = lazy(() => import("./Screens/leave-all"));
const SalaryProcess = lazy(() => import("./Screens/p-salary-process"));
const FinalSettlement = lazy(() => import("./Screens/p-final-sattlement"));
const SalaryRegister = lazy(() => import("./Screens/p-salary-register"));
const Reimbursement = lazy(() => import("./Screens/p-reimbursement"));
const Loans = lazy(() => import("./Screens/loans"));
const Survey = lazy(() => import("./Screens/survey"));
const Setting = lazy(() => import("./Screens/Setting"));
const Approvals = lazy(() => import("./Screens/approvals"));
const PunchTime = lazy(() => import("./Screens/emp-punch-time"));
const EmpViewDetail = lazy(() => import("./Screens/emp-view-details"));
const EmployeeViewProfile = lazy(() => import("./Screens/employee-master-view-profile"));
const EmployeeAddSalary = lazy(() => import("./Screens/employee-salary-master-add"));
const EmployeeEditSalary = lazy(() => import("./Screens/employee-salary-master-edit"));
const SalaryGenerate = lazy(() => import("./Screens/emp-salary-generate"));
const EmpSalarySlip = lazy(() => import("./Screens/emp-salary-slip"));
const BankDetails = lazy(() => import("./Screens/employee-bank-details"));
const LeaveCalendar = lazy(() => import("./Screens/employee-leave-calendar"));
const EditEmployee = lazy(() => import("./Screens/employee-master-edit-profile"));
const CompanyInformation = lazy(() => import("./Screens/Company-Information"));
const CompanyCategoryDepartment = lazy(() => import("./Screens/company-category-department"));
const CompanyUploadEmployee = lazy(() => import("./Screens/company-upload -employee"));
const CompanyCategoryDepartmentBranch = lazy(() => import("./Screens/company-cat-dep-branch"));
const CompanyCategoryDepartmentdepatment = lazy(() => import("./Screens/company-cat-dep-department"));
const CompanyCategoryDepartmentdesignation = lazy(() => import("./Screens/company-cat-dep-designation"));
const EmployeeAttendenceRegularization = lazy(() => import("./Screens/emp-attendence-regularization"));
const BankTransfer = lazy(() => import("./Screens/p-bank-transfer"));
const Createofferletter = lazy(() => import("./Screens/create-offer_letter"));
const OfferLetterLog = lazy(() => import("./Screens/offer-letter-log"));
const EPFO = lazy(() => import("./Screens/complains-epf"));
const ESIC = lazy(() => import("./Screens/complains-esic"));
const AdminSalarySlip = lazy(() => import("./Screens/admin-salary-slip"));
const Form16Download = lazy(() => import("./Screens/form-16-download"));
const EMPChangePassword = lazy(() => import("./Screens/emp-change-password"));
const Holidays = lazy(() => import("./Screens/holidays"));
const LeavePolicy = lazy(() => import("./Screens/leave-policy"));
const Announcements = lazy(() => import("./Screens/Announcements"));
const ReferralRequest = lazy(() => import("./Screens/ReferralRequest"));
const AdminReferrals = lazy(() => import("./Screens/AdminReferrals"));
const Profile = lazy(() => import("./Screens/Profile"));
const AdminProfileRequests = lazy(() => import("./Screens/AdminProfileRequests"));
const AdminActiveJobs = lazy(() => import("./Screens/AdminActiveJobs"));
const EmpActiveJobs = lazy(() => import("./Screens/EmpActiveJobs"));
const EmpSalaryStructure = lazy(() => import("./Screens/EmpSalaryStructure"));
const EmpExitDocuments = lazy(() => import("./Screens/EmpExitDocuments"));
const EmpOfferLetter = lazy(() => import("./Screens/EmpOfferLetter"));
const EmpConfirmationLetter = lazy(() => import("./Screens/EmpConfirmationLetter"));

const tabToRouteMap = {
  // Admin tabs
  "dashboard": "/dashboard",
  "On board": "/onboard",
  "Create letter": "/onboard/create-letter",
  "Document entry": "/onboard/document-entry",
  "Assets details": "/onboard/assets-details",
  "Assets assignment": "/onboard/assets-assignment",
  "All On board ": "/onboard/all",
  "Offer letter": "/onboard/offer-letter",
  "Offer letter log": "/onboard/offer-letter-log",
  "Referrals": "/onboard/referral-requests",
  "ActiveJobs": "/active-jobs",
  "Employees": "/employees",
  "Employee master": "/employees/employee-master",
  "Salary master": "/employees/salary-master",
  "Employee details": "/employees/employee-details",
  "CTC / earning": "/employees/ctc-earning",
  "Category": "/employees/category",
  "Calendar": "/employees/calendar",
  "All employees": "/employees/all",
  "Attendence": "/attendance",
  "Attendence regularization": "/attendance/regularization",
  "Submit attendance": "/attendance/submit",
  "In out activities": "/attendance/in-out-activities",
  "Daily attendence": "/attendance/daily-attendance",
  "Monthly attendence": "/attendance/monthly-attendance",
  "Manage shift": "/attendance/manage-shift",
  "All Attendence": "/attendance/all",
  "View Uploaded Attendance": "/attendance/view-uploaded",
  "Leave": "/leave",

  "Apply leave": "/leave/apply",
  "Leave balance": "/leave/balance",
  "All Leaves": "/leave/all",
  "Payroll": "/payroll",
  "Salary process": "/payroll/salary-process",
  "Final settlement": "/payroll/final-settlement",
  "Bank transfer": "/payroll/bank-transfer",
  "Salary paySilp": "/payroll/salary-payslip",
  "Form 16": "/payroll/form16",
  "Salary register": "/payroll/salary-register",
  "Reimbursement": "/payroll/reimbursement",
  "All payroll": "/payroll/all",
  "Loans": "/loans",
  "Survey": "/survey",
  "Settings": "/settings",
  "Settings-Organization": "/settings?menu=Organization",
  "Settings-Attendance": "/settings?menu=Attendance",
  "Settings-Leave": "/settings?menu=Leave",
  "Settings-Payroll": "/settings?menu=Payroll",
  "Settings-Notifications": "/settings?menu=Notifications",
  "Settings-Password": "/settings?menu=Password",
  "EditEmployee": "/employees/edit-employee",
  "BankDetail": "/payroll/add-bank-details",
  "LeaveCalendar": "/leave/calendar",
  "EPF": "/complains/epf",
  "ESIC": "/complains/esic",
  "Holidays": "/holidays",
  "Leave Policy": "/leave/policy",
  "Approvals": "/approvals",
  "Approved Leaves": "/approved-leaves",
  "Announcements": "/employees/announcements",
  "Profile": "/profile",
  "ProfileRequests": "/employees/profile-requests",

  // Employee tabs
  "PunchTime": "/punch",
  "Attendance Regularization": "/attendance/regularization",
  "Attendence Regularization": "/attendance/regularization",
  "AttendenceRegularization": "/attendance/regularization",
  "salarygenerate": "/payroll/salary-payslip",
  "EmpSalarySlip": "/payroll/salary-slip",
  "EmpSalaryStructure": "/payroll/salary-structure",
  "EmpOfferLetter": "/payroll/offer-letter",
  "EmpExitDocuments": "/payroll/exit-documents",
  "Change Password": "/change-password",
  "Referral Request": "/referral-request",
  "companyinfo": "/company/info",
  "companycategory": "/company/category",
  "companyuploademp": "/company/upload-emp",
  "categorybranch": "/company/branch",
  "categorydepartment": "/company/department",
  "categorydesignation": "/company/designation",
};

// Dispatcher Component for Dashboards based on role
const DashboardDispatcher = ({ setCurrentTab }) => {
  const role = getUserRole();
  if (role === 'admin') {
    return <AdminDashboard setCurrentTab={setCurrentTab} />;
  }
  return <EmpDashboard setCurrentTab={setCurrentTab} />;
};

// Dispatcher Component for Active Jobs based on role
const ActiveJobsDispatcher = ({ setCurrentTab, onBack }) => {
  const role = getUserRole();
  if (role === 'admin') {
    return <AdminActiveJobs setCurrentTab={setCurrentTab} />;
  }
  return <EmpActiveJobs setCurrentTab={setCurrentTab} onBack={onBack} />;
};

// Dispatcher Component for Attendance Regularization based on role
const AttendanceRegularizationDispatcher = ({ onBack }) => {
  const role = getUserRole();
  if (role === 'admin') {
    return <AttendanceRegularization />;
  }
  return <EmployeeAttendenceRegularization onBack={onBack} />;
};

// Dispatcher Component for Holidays based on role
const HolidaysDispatcher = ({ onBack }) => {
  const role = getUserRole();
  if (role === 'admin') {
    return <Holidays />;
  }
  return <EmployeeCalendar onBack={onBack} />;
};

// Dispatcher Component for Leave Policy based on role
const LeavePolicyDispatcher = ({ onBack }) => {
  const role = getUserRole();
  if (role === 'admin') {
    return <LeavePolicy />;
  }
  return <LeavePolicy onBack={onBack} />;
};

// Dispatcher Component for Announcements based on role
const AnnouncementsDispatcher = ({ setCurrentTab, onBack }) => {
  const role = getUserRole();
  if (role === 'admin') {
    return <Announcements setCurrentTab={setCurrentTab} />;
  }
  return <Announcements setCurrentTab={setCurrentTab} onBack={onBack} />;
};

const App = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout, loading } = useContext(AuthContext);

  useEffect(() => {
    // Session validity check on mount
    const role = getUserRole();
    if (isAuthenticated && (role === 'undefined' || !role)) {
      console.log("Invalid session detected. Logging out...");
      logout();
    }
  }, [isAuthenticated, logout]);

  const setCurrentTab = (tabName) => {
    const route = tabToRouteMap[tabName];
    if (route) {
      navigate(route);
    } else {
      console.warn(`No route configured for tab: ${tabName}`);
    }
  };

  const onBack = () => {
    navigate("/dashboard");
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <PWAPrompts />
      <Routes>
        {/* Public Route */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Protected Routes Wrapper */}
        <Route
          path="/"
          element={
            <AuthGuard>
              <Layout />
            </AuthGuard>
          }
        >
          {/* Redirect from root index to /dashboard */}
          <Route index element={<Navigate to="/dashboard" replace />} />
          
          {/* Dispatcher Dashboard */}
          <Route path="dashboard" element={<DashboardDispatcher setCurrentTab={setCurrentTab} />} />

          {/* On Boarding */}
          <Route path="onboard" element={<OnBoard />} />
          <Route path="onboard/create-letter" element={<CreateLetter />} />
          <Route path="onboard/document-entry" element={<DocumentEntry />} />
          <Route path="onboard/assets-details" element={<AssetsDetails />} />
          <Route path="onboard/assets-assignment" element={<AssetsAssignment />} />
          <Route path="onboard/all" element={<OnBoardAll />} />
          <Route path="onboard/offer-letter" element={<Createofferletter />} />
          <Route path="onboard/offer-letter-log" element={<OfferLetterLog />} />
          <Route path="onboard/referral-requests" element={<AdminReferrals setCurrentTab={setCurrentTab} />} />
          <Route path="active-jobs" element={<ActiveJobsDispatcher setCurrentTab={setCurrentTab} onBack={onBack} />} />

          {/* Employees */}
          <Route path="employees" element={<Employees />} />
          <Route path="employees/employee-master" element={<EmployeeMaster onBack={onBack} />} />
          <Route path="employees/salary-master" element={<EmployeeSalaryMaster />} />
          <Route path="employees/employee-details" element={<EmployeeDetails />} />
          <Route path="employees/ctc-earning" element={<EmployeeCTC />} />
          <Route path="employees/category" element={<EmployeeCategory />} />
          <Route path="employees/calendar" element={<EmployeeCalendar />} />
          <Route path="employees/all" element={<EmployeeAll />} />
          <Route path="employees/edit-employee" element={<EditEmployee />} />
          <Route path="employees/profile-requests" element={<AdminProfileRequests onBack={onBack} />} />
          <Route path="employees/announcements" element={<AnnouncementsDispatcher setCurrentTab={setCurrentTab} onBack={onBack} />} />

          {/* Attendance */}
          <Route path="attendance" element={<Attendence />} />
          <Route path="attendance/regularization" element={<AttendanceRegularizationDispatcher onBack={onBack} />} />
          <Route path="attendance/submit" element={<SubmitAttendence />} />
          <Route path="attendance/in-out-activities" element={<InOutActivities />} />
          <Route path="attendance/daily-attendance" element={<DailyAttendence />} />
          <Route path="attendance/monthly-attendance" element={<MonthlyAttendence />} />
          <Route path="attendance/manage-shift" element={<ManageShift />} />
          <Route path="attendance/all" element={<AllAttendence />} />
          <Route path="attendance/view-uploaded" element={<ViewUploadedAttendance />} />


          {/* Leave */}
          <Route path="leave" element={<Leave />} />
          <Route path="leave/apply" element={<ApplyLeave onBack={onBack} />} />
          <Route path="leave/balance" element={<LeaveBalance onBack={onBack} />} />
          <Route path="leave/all" element={<AllLeaves />} />
          <Route path="leave/policy" element={<LeavePolicyDispatcher onBack={onBack} />} />
          <Route path="leave/calendar" element={<LeaveCalendar />} />

          {/* Payroll */}
          <Route path="payroll" element={<Navigate to="/payroll/salary-process" replace />} />
          <Route path="payroll/salary-process" element={<SalaryProcess />} />
          <Route path="payroll/final-settlement" element={<FinalSettlement />} />
          <Route path="payroll/bank-transfer" element={<BankTransfer />} />
          <Route path="payroll/salary-payslip" element={<AdminSalarySlip />} />
          <Route path="payroll/form16" element={<Form16Download />} />
          <Route path="payroll/salary-slip" element={<EmpSalarySlip onBack={onBack} />} />
          <Route path="payroll/salary-structure" element={<EmpSalaryStructure />} />
          <Route path="payroll/offer-letter" element={<EmpOfferLetter />} />
          <Route path="payroll/confirmation-letter" element={<EmpConfirmationLetter />} />
          <Route path="payroll/exit-documents" element={<EmpExitDocuments />} />
          <Route path="payroll/salary-register" element={<SalaryRegister />} /> {/* SalaryRegister */}
          <Route path="payroll/reimbursement" element={<Reimbursement />} />
          <Route path="payroll/add-bank-details" element={<BankDetails />} />

          {/* Complains */}
          <Route path="complains/epf" element={<EPFO />} />
          <Route path="complains/esic" element={<ESIC />} />

          {/* Other Sections */}
          <Route path="loans" element={<Loans />} />
          <Route path="survey" element={<Survey />} />
          <Route path="settings" element={<Setting />} />
          <Route path="profile" element={getUserRole() === 'admin' ? <Navigate to="/dashboard" replace /> : <Profile onBack={onBack} />} />
          <Route path="punch" element={<PunchTime onBack={onBack} />} />
          <Route path="holidays" element={<HolidaysDispatcher onBack={onBack} />} />
          <Route path="approvals" element={<Approvals setApprovals={onBack} />} />
          <Route path="approved-leaves" element={<Approvals setApprovals={onBack} initialSubTab="approved" />} />

          {/* Employee Specific Company Settings */}
          <Route path="company/info" element={<CompanyInformation />} />
          <Route path="company/category" element={<CompanyCategoryDepartment />} />
          <Route path="company/upload-emp" element={<CompanyUploadEmployee />} />
          <Route path="company/branch" element={<CompanyCategoryDepartmentBranch />} />
          <Route path="company/department" element={<CompanyCategoryDepartmentdepatment />} />
          <Route path="company/designation" element={<CompanyCategoryDepartmentdesignation />} />

          {/* General Routes */}
          <Route path="change-password" element={<EMPChangePassword onBack={onBack} />} />
          <Route path="referral-request" element={<ReferralRequest setCurrentTab={setCurrentTab} onBack={onBack} />} />
        </Route>

        {/* Fallback Redirection */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default App;